// Source: D:/AI/site-queensbutler/docs/structure/04_詳細設計_ページ内容スキーマ.md §4
import { useLocation } from 'react-router-dom'
import { BlockRenderer } from '@/components/blocks/BlockRenderer'
import { Cta } from '@/components/blocks/Cta'
import { BookIntro } from '@/components/blocks/BookIntro'
import { Billboard } from '@/components/layout/Billboard'
import { Breadcrumb, type BreadcrumbItem } from '@/components/seo/Breadcrumb'
import { PageHead } from '@/components/seo/PageHead'
import { buildPageStructuredData } from '@/components/seo/structuredData'
import type { FixedRouteData } from '@/data/types'
import type { CardsBlock } from '@/lib/contentTypes'
import type { PageContent } from '@/lib/contentTypes'
import { createInterimContent, getPageContent, getRoute, getRouteAncestors, normalizePageUrl, pageLabel } from '@/lib/pageContent'
import { shopPageContent, shopPageContentEn, shopRoutes } from '@/router/shopData'
import { getLang, stripEn, withLang } from '@/lib/lang'
import { ui } from '@/data/nav.en'
import { shopLive, ShopPreparing } from '@/lib/shopLive'

declare const __QB_ALLOW_MISSING__: boolean

const shopRouteByUrl = new Map<string, FixedRouteData>(shopRoutes.map((route) => [route.url, route]))
const shopPageByUrl = new Map<string, PageContent>([...shopPageContent, ...shopPageContentEn].map((page) => [page.url, page]))

function interimIsAllowed(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_QB_ALLOW_MISSING === '1' ||
    (typeof __QB_ALLOW_MISSING__ !== 'undefined' && __QB_ALLOW_MISSING__)
}

export function Page() {
  const { pathname } = useLocation()
  const normalizedPath = normalizePageUrl(pathname)
  // 言語は pathname から判定。ルート(routes.json / shop-pages.json)は JA URL で引き、本文は言語別に引く
  const lang = getLang(normalizedPath)
  const t = ui[lang]
  const shopRoute = shopRouteByUrl.get(stripEn(normalizedPath))
  const route = getRoute(pathname) ?? shopRoute
  if (!route) return null

  const authored = getPageContent(pathname) ?? shopPageByUrl.get(normalizedPath)
    ?? (lang === 'en' && interimIsAllowed() ? shopPageByUrl.get(stripEn(normalizedPath)) : undefined)
  if (!authored && !interimIsAllowed()) {
    throw new Error(`[page-content] ${route.id} (${route.url}) の本文JSONがありません。QB_ALLOW_MISSING=1 は暫定確認時だけ使用できます。`)
  }
  const page0 = authored ?? createInterimContent(route)
  // Yuichi 2026-09-17「商品画像を出すな」「この商品はテストで作ったもので売り物じゃない」: 本番(VITE_QB_SHOP_LIVE 未設定)のショップ配下(問い合わせ除く)は
  // ビルボード・og:image・JSON-LD からも商品画像/商品データを外し、本文は準備中案内だけにする
  const shopPreparing = page0.service === 'shop' && !shopLive && !stripEn(page0.url).startsWith('/shop/contact/')
  const SHOP_HUB_IMAGE = '/img/scenes/a08_plates_flatlay.jpg'
  const isShopItem = shopPreparing && (page0.layout === 'shop-item' || (stripEn(page0.url).startsWith('/shop/items/') && stripEn(page0.url) !== '/shop/items/'))
  const prepTitle = lang === 'en' ? 'Online Shop — Coming Soon | Queen\'s Butler' : 'オンラインショップ(準備中)｜Queen\'s Butler'
  const prepH1 = lang === 'en' ? 'Online Shop' : 'オンラインショップ'
  const prepDef = lang === 'en'
    ? 'The Queen\'s Butler Online Shop is currently being prepared. Details will be announced here once ready.'
    : 'Queen\'s Butler のオンラインショップは、只今準備中でございます。ご案内が整い次第、こちらでお知らせいたします。'
  const page = shopPreparing
    ? { ...page0,
        // 商品詳細は商品名・説明・タイトルも出さない(Yuichi 2026-09-17「この商品はテストで作ったもので売り物じゃない」)
        // 準備中のあいだは定義文・description も汎用文言(ハブ/コレクションの定義文が商品名に触れているため)
        definition: prepDef,
        ...(isShopItem ? { h1: prepH1, h1Sub: "Queen's Butler The Shop", meta: { ...page0.meta, navLabel: prepH1, title: prepTitle, description: prepDef, ogImage: SHOP_HUB_IMAGE, noindex: true } } : { meta: { ...page0.meta, description: prepDef, ogImage: SHOP_HUB_IMAGE } }),
        billboard: { ...page0.billboard, image: SHOP_HUB_IMAGE, eyebrow: 'The Shop', alt: lang === 'en' ? 'The Shop — Queen\'s Butler' : 'オンラインショップ — Queen\'s Butler' },
        structuredData: undefined, sections: [], related: undefined }
    : page0
  const breadcrumbs: BreadcrumbItem[] = [
    ...getRouteAncestors(route).map((item) => ({ label: pageLabel(item, lang), path: withLang(item.url, lang) })),
    ...(route.url === '/' ? [] : [{ label: isShopItem ? prepH1 : lang === 'en' ? (page.meta.navLabel ?? page.h1) : route.name }]),
  ]
  const relatedBlock: CardsBlock | undefined = page.related?.length ? {
    type: 'cards', pattern: 'C-3a', eyebrow: t.relatedEyebrow, heading: t.related, items: page.related,
  } : undefined

  return (
    <main className="page-main flex-1" data-service={page.service ?? 'common'}>
      <PageHead
        title={page.meta.title}
        description={page.meta.description}
        path={page.url}
        canonical={page.meta.canonical}
        ogImage={page.meta.ogImage}
        ogType={page.layout === 'article' ? 'article' : page.layout === 'shop-item' || page.structuredData?.product ? 'product' : 'website'}
        noindex={!authored || page.meta.noindex}
        jsonLd={buildPageStructuredData(page, isShopItem ? { ...route, name: prepH1 } : route)}
      />
      <Billboard billboard={page.billboard} h1={page.h1} h1Sub={page.h1Sub} service={page.service ?? undefined} />
      {route.url !== '/' && <Breadcrumb items={breadcrumbs} />}
      {/* Yuichi 2026-09-17「問い合わせフォームの前のくだりがながくて、実際のフォームまでが遠い」→「(カードの)セクションは お問い合わせ・ご予約とは の下において」: 先頭がフォームのページは定義文の直下にフォーム(sections[0])が来る。前置きの他セクションはフォームの後ろ */}
      {page.layout === 'service-top' ? (
        <section className="def-band" aria-labelledby={`${page.id}-definition`}>
          <div className="shell">
            <h2 id={`${page.id}-definition`} className="sr-only">{t.guideOf(page.h1)}</h2>
            <p className="def">{page.definition}</p>
          </div>
        </section>
      ) : (
        <section className="defn" aria-labelledby={`${page.id}-definition`}>
          <div className="shell">
            {!authored && <p className="eyebrow">{t.interim}</p>}
            <div className="defn-eyebrow">Queen's Butler — Definition</div>
            <h2 id={`${page.id}-definition`} className="defn-title">{lang === 'en' ? t.definitionOf(page.meta.navLabel ?? page.h1) : t.definitionOf(page.definition.match(/^(.{2,40}?)とは、/)?.[1] ?? page.h1)}</h2>
            <div className="defn-orn" aria-hidden="true"><span /><i /><span /></div>
            <p className="def">{page.definition}</p>
          </div>
        </section>
      )}
      {route.url === '/' && <BookIntro />}
      {/* Yuichi 2026-09-17「ショップが非表示になっていない」「本番デプロイする際はオンラインショップの商品は一覧、詳細ページ含めて準備中にしろよ。かならず」: 本番(VITE_QB_SHOP_LIVE 未設定)ではショップ配下の全ページを準備中表示だけにする(定義文までは残し、カテゴリー/コレクション/ガイド/FAQ 等の本文は出さない)。問い合わせページは除く */}
      {shopPreparing ? (
        <div className="shell sec tight"><ShopPreparing /></div>
      ) : (<>
        {page.sections.map((block, index) => <BlockRenderer key={`${block.type}-${index}`} block={block} />)}
        {relatedBlock && <BlockRenderer block={relatedBlock} />}
        {page.cta && <Cta {...page.cta} />}
      </>)}
    </main>
  )
}
